import * as React from "react";

import {
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
} from "../../components/ui/context-menu";
import { Popover, PopoverPopup } from "../../components/ui/popover";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// DataTable overlay host
//
// ONE popover root and ONE context-menu root for the whole table, anchored to
// whichever cell or row was activated.
//
// The alternative — a Base UI root per cell and per row — is what makes a
// virtualized table stutter. A root is not free at rest: it builds a store, a
// `FloatingTree`, and a dozen-plus hooks before anything is on screen. Multiply
// that by cells x rows and re-pay it every time a row recycles under the
// scroll, and the overlay machinery dominates every scroll frame while the user
// has not opened a single menu. Hoisting the roots out of the row makes the
// resting cost constant instead of proportional to the viewport, so cells
// declare CONTENT and the table owns the overlay.
//
// Base UI supports this directly: `Menu.Positioner` resolves `anchorProp ??
// parent.context.anchor`, so an explicit anchor wins, and `ContextMenu.Root`
// already keeps a cursor-positioned virtual anchor that its trigger sets. The
// single `ContextMenuTrigger` wraps the whole scroll container, so the open
// gesture (right click, touch long-press, the mouse-up dance) stays Base UI's,
// and only the popup's CONTENT is chosen per target.
// ---------------------------------------------------------------------------

/** A popup's content, resolved lazily so nothing is built until it opens. */
export type DataTableMenuRequest = Omittable<{
  render: () => React.ReactNode;
}>;

export type DataTableCellPopoverRequest = Omittable<{
  /** Identity of the requesting cell, so it can render its `active` state. */
  id: string;
  /** Element the popover positions against (the cell trigger). */
  anchor: HTMLElement;
  content: (close: () => void) => React.ReactNode;
  contentProps?: Omit<React.ComponentProps<typeof PopoverPopup>, "children">;
  onOpenChange?: (open: boolean) => void;
}>;

type DataTableOverlayContextValue = {
  /**
   * Claim the next context-menu gesture. Called from a `pointerdown` with
   * button 2 — a SEPARATE event dispatch from the `contextmenu` that follows,
   * so React has flushed this before Base UI decides whether to open.
   */
  claimContextMenu: (request: DataTableMenuRequest | null) => void;
  openCellPopover: (request: DataTableCellPopoverRequest) => void;
  closeCellPopover: () => void;
  /** Id of the cell whose popover is open, for `active` trigger styling. */
  activePopoverId: string | null;
};

const DataTableOverlayContext =
  React.createContext<DataTableOverlayContextValue | null>(null);

/**
 * Overlay host for cell popovers and the row/header context menu. Returns null
 * outside a DataTable, which is the signal for a cell to fall back to owning
 * its own popover (the component is exported and usable standalone).
 */
export function useDataTableOverlay(): DataTableOverlayContextValue | null {
  return React.useContext(DataTableOverlayContext);
}

export function DataTableOverlayProvider({
  scrollContainer,
}: {
  /**
   * The table's scroll container, children included. It doubles as the single
   * `ContextMenuTrigger`, so Base UI's open gesture (right click, touch
   * long-press, the mouse-up dance) is unchanged — only the popup's content is
   * chosen per target.
   */
  scrollContainer: React.ReactElement<Record<string, unknown>>;
}): React.ReactElement {
  const [menu, setMenu] = React.useState<DataTableMenuRequest | null>(null);
  const [popover, setPopover] =
    React.useState<DataTableCellPopoverRequest | null>(null);

  const claimContextMenu = React.useCallback(
    (request: DataTableMenuRequest | null) => setMenu(request),
    [],
  );

  // Mirrors `popover` for the event handlers below. Notifying a cell and
  // moving focus are SIDE EFFECTS, and a `useState` updater must be pure —
  // React is free to invoke one more than once and under StrictMode always
  // does. Running them inside the updater fired a stray close/reopen pair, so
  // re-pressing an open cell never toggled it shut and a draft-on-close cell
  // committed and then reset on a single press. The ref is the current value
  // at dispatch time; the effects run outside the updater, exactly once.
  const popoverRef = React.useRef<DataTableCellPopoverRequest | null>(null);

  const commitPopover = React.useCallback(
    (next: DataTableCellPopoverRequest | null) => {
      popoverRef.current = next;
      setPopover(next);
    },
    [],
  );

  const openCellPopover = React.useCallback(
    (request: DataTableCellPopoverRequest) => {
      const current = popoverRef.current;
      // Re-pressing the open cell's trigger toggles it shut, matching the
      // per-cell popover this replaced.
      if (current?.id === request.id) {
        commitPopover(null);
        current.onOpenChange?.(false);
        return;
      }
      commitPopover(request);
      current?.onOpenChange?.(false);
      request.onOpenChange?.(true);
    },
    [commitPopover],
  );

  const closeCellPopover = React.useCallback(() => {
    const current = popoverRef.current;
    if (current === null) return;
    commitPopover(null);
    current.onOpenChange?.(false);
    // Without a Popover.Trigger part there is nothing for Base UI to return
    // focus to, so hand it back to the cell that opened the popover.
    current.anchor.focus?.();
  }, [commitPopover]);

  const handlePopoverOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) closeCellPopover();
    },
    [closeCellPopover],
  );

  const handleMenuOpenChange = React.useCallback((open: boolean) => {
    if (!open) setMenu(null);
  }, []);

  const value = React.useMemo<DataTableOverlayContextValue>(
    () => ({
      claimContextMenu,
      openCellPopover,
      closeCellPopover,
      activePopoverId: popover?.id ?? null,
    }),
    [claimContextMenu, openCellPopover, closeCellPopover, popover?.id],
  );

  // Capture-phase reset: runs before any row's or header's bubble-phase claim,
  // in the SAME pointerdown dispatch, so a right click that lands on empty
  // table space leaves `menu` null instead of reopening the last target's menu.
  const trigger = React.useMemo(
    () =>
      React.cloneElement(scrollContainer, {
        onPointerDownCapture: (event: React.PointerEvent) => {
          if (event.button === 2) setMenu(null);
          (
            scrollContainer.props.onPointerDownCapture as
              | ((event: React.PointerEvent) => void)
              | undefined
          )?.(event);
        },
      }),
    [scrollContainer],
  );

  return (
    <DataTableOverlayContext.Provider value={value}>
      {/* `disabled` comes from the pointerdown claim, so a right click on
          empty table space below the rows falls through to the browser's own
          menu instead of opening an empty popup. */}
      <ContextMenu disabled={menu === null} onOpenChange={handleMenuOpenChange}>
        <ContextMenuTrigger render={trigger} />
        {menu !== null ? (
          <ContextMenuPopup>{menu.render()}</ContextMenuPopup>
        ) : null}
      </ContextMenu>
      <Popover open={popover !== null} onOpenChange={handlePopoverOpenChange}>
        {popover !== null ? (
          <PopoverPopup
            align="start"
            anchor={popover.anchor}
            {...popover.contentProps}
          >
            {popover.content(closeCellPopover)}
          </PopoverPopup>
        ) : null}
      </Popover>
    </DataTableOverlayContext.Provider>
  );
}

/**
 * Returns a factory for `onPointerDown` handlers that claim the next
 * context-menu gesture. Call it per row or header cell with that target's
 * content thunk; what lands on the element is a plain closure, which is the
 * entire point — no overlay root enters the row.
 *
 * Returns undefined outside a DataTable so callers can skip the wiring.
 */
export function useContextMenuClaim():
  | ((render: () => React.ReactNode) => (event: React.PointerEvent) => void)
  | undefined {
  const claim = useDataTableOverlay()?.claimContextMenu;

  return React.useMemo(() => {
    if (!claim) return undefined;
    return (render: () => React.ReactNode) => (event: React.PointerEvent) => {
      // Only the right button opens a context menu; a left-click drag or a
      // selection press must not arm one.
      if (event.button !== 2) return;
      claim({ render });
    };
  }, [claim]);
}
